import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FormNavigation from 'components/FormNavigation';
import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import { FlowComponentType } from 'routes/types';
import { CurrentFlow } from 'enums/CurrentFlow';
import { YourIncomeVariable } from 'enums/LoanFormVariables';
import { setApplicationFlow } from 'handlers/applicationStep';
import { useNavigate } from 'hooks/useNavigate';
import { getReapplyData, getReapplyDisplayData } from 'selectors/reapplyData';
import { getYourContactData } from 'selectors/getYourContact';
import { getYourIncome } from 'selectors/yourIncome';
import { getYourEmployer } from 'selectors/yourEmployer';
import { RootState } from 'handlers';
import { resetEmail } from 'handlers/yourContact';
import { resetYourDetailsData } from 'handlers/yourAddress';
import { setAdditionalIncome } from 'handlers/yourEmployer';
import { resetYourIncomeAmount, resetYourIncomeCredentialDate, resetYourIncomeEmployer } from 'handlers/yourIncome';
import { YourEmployerEditFocus, YourEmployerState } from 'components/LoanForm/Employer/YourEmployer';
import {
  ReapplyEditField,
  clearReapplyEdit,
  patchReapplyData,
  setIsReapplying,
  startReapplyEdit,
} from 'handlers/reapplyData';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';

import DisabledDetailField from './DisabledDetailField';
import { EDIT_FIELD_TO_ROUTE, formatAddress, formatCredentialDate, formatLast4SSN } from './confirmReapplyDetailsUtils';

import styles from './ConfirmReapplyDetails.module.scss';

const ConfirmReapplyDetails = ({ navigationInfo, handleNext }: FlowComponentType): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentEditField, isReapplying } = useSelector(getReapplyData);
  const data = useSelector(getReapplyDisplayData);
  const { email } = useSelector(getYourContactData);
  const yourIncome = useSelector(getYourIncome);
  const { additionalIncome } = useSelector(getYourEmployer);
  const { address_line_1: street, city, zip_code: zipCode, argyle_state: addressState } = useSelector(
    (reduxState: RootState) => reduxState.yourAddress,
  );

  useEffect(() => {
    if (!currentEditField || !data) {
      dispatch(clearReapplyEdit());
      return;
    }

    switch (currentEditField) {
      case ReapplyEditField.Email:
        if (email) dispatch(patchReapplyData({ email }));
        break;
      case ReapplyEditField.Address:
        if (street) {
          dispatch(
            patchReapplyData({
              address: {
                street,
                city,
                stateOrProvince: addressState,
                zipOrPostalCode: zipCode,
                country: data.address?.country || 'US',
              },
            }),
          );
        }
        break;
      case ReapplyEditField.Employment: {
        const employerName = yourIncome[YourIncomeVariable.EmployerName];
        const notEmployed = yourIncome[YourIncomeVariable.NotEmployed];
        const annualIncome = yourIncome[YourIncomeVariable.TotalAnnualIncome];
        if (!employerName && !notEmployed) break;
        dispatch(
          patchReapplyData({
            primaryEmployerName: notEmployed ? '' : employerName,
            notEmployed,
            ...(annualIncome != null && { totalAnnualIncome: annualIncome as number }),
          }),
        );
        break;
      }
      case ReapplyEditField.CredentialDate: {
        const credentialDate = yourIncome[YourIncomeVariable.CredentialAcquisitionDate];
        if (!credentialDate) break;
        dispatch(patchReapplyData({ credentialsAcquisitionDate: credentialDate }));
        break;
      }
      case ReapplyEditField.Income:
        if (yourIncome[YourIncomeVariable.TotalAnnualIncome] != null) {
          dispatch(patchReapplyData({ totalAnnualIncome: yourIncome[YourIncomeVariable.TotalAnnualIncome] as number }));
        }
        break;
      case ReapplyEditField.AdditionalIncome:
        dispatch(patchReapplyData({ additionalIncome }));
        break;
      default:
        break;
    }

    dispatch(clearReapplyEdit());
  }, []);

  const handleEdit = (field: ReapplyEditField) => {
    switch (field) {
      case ReapplyEditField.Email:
        dispatch(resetEmail());
        break;
      case ReapplyEditField.Address:
        dispatch(resetYourDetailsData());
        break;
      case ReapplyEditField.Employment:
        dispatch(resetYourIncomeEmployer());
        dispatch(resetYourIncomeAmount());
        break;
      case ReapplyEditField.CredentialDate:
        dispatch(resetYourIncomeCredentialDate());
        break;
      case ReapplyEditField.Income:
        dispatch(resetYourIncomeAmount());
        break;
      case ReapplyEditField.AdditionalIncome:
        dispatch(setAdditionalIncome(undefined));
        break;
      default:
        break;
    }

    dispatch(startReapplyEdit(field));

    let editFocus: YourEmployerEditFocus | undefined;
    if (field === ReapplyEditField.Employment) editFocus = YourEmployerEditFocus.Employer;
    if (field === ReapplyEditField.CredentialDate) editFocus = YourEmployerEditFocus.CredentialDate;

    navigate(EDIT_FIELD_TO_ROUTE[field], editFocus ? { state: { editFocus } as YourEmployerState } : undefined);
  };

  const handleContinue = () => {
    dispatch(setApplicationFlow({ currentFlow: CurrentFlow.DebtConsolidation }));
    dispatch(setIsReapplying(true));
    handleNext();
  };

  if (!data) return <></>;

  return (
    <>
      <FormNavigation {...navigationInfo} {...(isReapplying && { title: '' })} />
      <FormContainer
        title="Confirm your details"
        subtitle="We found this information associated with your phone number. Please confirm it looks correct."
      >
        <div className={styles.fields}>
          {data.email && (
            <DisabledDetailField label="Email" value={data.email} onEdit={() => handleEdit(ReapplyEditField.Email)} />
          )}
          {data.address && (
            <DisabledDetailField
              label="Address"
              value={formatAddress(data.address)}
              onEdit={() => handleEdit(ReapplyEditField.Address)}
            />
          )}
          {data.dateOfBirth && <DisabledDetailField label="Date of birth" value={data.dateOfBirth} />}
          {data.last4SSN && (
            <DisabledDetailField label="Social Security number" value={formatLast4SSN(data.last4SSN)} />
          )}
          {data.notEmployed ? (
            <DisabledDetailField
              label="Employment"
              value="Not currently employed"
              onEdit={() => handleEdit(ReapplyEditField.Employment)}
            />
          ) : (
            data.primaryEmployerName && (
              <DisabledDetailField
                label="Primary employer"
                value={data.primaryEmployerName}
                onEdit={() => handleEdit(ReapplyEditField.Employment)}
              />
            )
          )}
          {!data.notEmployed && data.credentialsAcquisitionDate && (
            <DisabledDetailField
              label="Credential date"
              value={formatCredentialDate(data.credentialsAcquisitionDate)}
              onEdit={() => handleEdit(ReapplyEditField.CredentialDate)}
            />
          )}
          {data.totalAnnualIncome != null && (
            <DisabledDetailField
              label="Gross annual income"
              value={formatMonetaryAmount(data.totalAnnualIncome)}
              onEdit={() => handleEdit(ReapplyEditField.Income)}
            />
          )}
          {data.additionalIncome != null && (
            <DisabledDetailField
              label="Additional income"
              value={formatMonetaryAmount(data.additionalIncome)}
              onEdit={() => handleEdit(ReapplyEditField.AdditionalIncome)}
            />
          )}
        </div>

        <Button onClick={handleContinue}>This is correct</Button>
      </FormContainer>
    </>
  );
};

export default ConfirmReapplyDetails;
